import { useSession } from "@store/session";
import { useQuery } from "@tanstack/react-query";
import type { User } from "@types";
import { safeConsole } from "@utils";
import { useEffect } from "react";
import {
  type AuthenticatedClientFactory,
  createAuthenticatedClient,
} from "./apiClient";
import { queryKeys } from "./queryKeys";

// Refetch cadence (SD-10888): on mount + on window focus + a background interval,
// so a mid-session role/company/permission change re-projects into the FE even
// without a refocus. The backend enforces authorization live per request
// (SD-10871) — this keeps the FE projection (nav / guards / <Can>) in sync with
// that truth. staleTime < interval so focus + interval don't double-fetch
// back-to-back.
const REFETCH_INTERVAL_MS = 180_000; // 3 min
const STALE_TIME_MS = 60_000;

export const fetchCurrentUser = async (
  accessToken: string,
  /**
   * React Query's per-fetch abort signal. Required, not optional: this query is
   * mounted high in the authed tree and refetches on an interval and on focus,
   * so a background request is regularly in flight when the user navigates or
   * switches company. Without a signal it is uncancellable and dies at the
   * network layer as a reported `ERR_NETWORK` instead of a clean abort.
   */
  signal: AbortSignal,
  createClient: AuthenticatedClientFactory<"get"> = createAuthenticatedClient,
  identityScope: "customer" | "partner" = "customer",
): Promise<User> => {
  if (!accessToken) throw new Error("Access token is required");
  const response = await createClient(accessToken).get(
    identityScope === "partner" ? "/partners/me" : "/users/me",
    {
      signal,
      timeout: 0,
    },
  );
  return response.data;
};

/**
 * Whether the active company is no longer accessible for `user` and must be
 * reset. `super`/`owner` reach any company (the backend validates against the
 * account, not `companyIds`), so they never need a reset; other roles are
 * limited to their `companyIds`.
 */
export const companyNeedsReset = (
  user: User,
  activeCompanyId: string | undefined,
): boolean => {
  if (!activeCompanyId) return false;
  if (user.role === "super" || user.role === "owner") return false;
  return !user.companyIds?.includes(activeCompanyId);
};

/**
 * Refetch the caller's live user and re-project it into the session, so the FE's
 * permission view tracks the server instead of the login-time snapshot. Mount
 * once high in the authed tree; inert (query disabled) when logged out.
 */
export const useGetCurrentUser = () => {
  const { accessToken, user, companyId, impersonation, setUser, setCompanyId } =
    useSession();
  /**
   * Whose `/users/me` this is.
   *
   * While a viewed-as session is open the effective token resolves to the
   * SUBJECT, so the identity being projected is theirs, not the staff user's.
   * Keying and guarding on the subject id is what lets the subject's own role,
   * reach and permissions land in the session — without it the query stays
   * disabled (the staff user is cleared at open) and the shell renders with no
   * identity at all.
   */
  const identityId = impersonation?.subjectId ?? user?.id;

  const query = useQuery({
    // Keyed by the session user id so a prior login's cached /me can never be
    // read into a different session.
    queryKey: queryKeys.global.currentUser(identityId),
    queryFn: ({ signal }) =>
      fetchCurrentUser(
        accessToken,
        signal,
        createAuthenticatedClient,
        user?.role === "partner" ? "partner" : "customer",
      ),
    enabled: Boolean(accessToken && identityId),
    refetchOnWindowFocus: true,
    refetchInterval: REFETCH_INTERVAL_MS,
    staleTime: STALE_TIME_MS,
  });

  const fresh = query.data;
  const { isError, error } = query;

  // Re-project the live user into the session (state.user + localStorage).
  // Guard `fresh.id === userId`: a cache entry from a prior login, or a response
  // that lands after logout, must never overwrite the current identity (mirrors
  // usePatchUser's guard). React Query's default structuralSharing keeps
  // `query.data` referentially stable across deep-equal refetches, so this
  // effect only fires when the user actually changed — no per-interval churn.
  useEffect(() => {
    if (fresh && fresh.id === identityId) setUser(fresh);
  }, [fresh, identityId, setUser]);

  // If the active company is no longer accessible, switch to a valid one so the
  // user isn't stranded on a screen whose every data call now 403s. When the
  // user has no accessible company, leave routing to redirect (the route guard
  // already denies).
  useEffect(() => {
    if (
      fresh &&
      fresh.id === identityId &&
      companyNeedsReset(fresh, companyId) &&
      fresh.companyIds?.length
    ) {
      setCompanyId(fresh.companyIds[0]);
    }
  }, [fresh, identityId, companyId, setCompanyId]);

  // Surface a failed background sync (rule: handle useQuery error). Non-fatal —
  // the session stays at its last-known-good snapshot and the backend still
  // enforces authz live, so a failed /me only delays FE re-projection.
  useEffect(() => {
    if (isError)
      safeConsole.error(
        "service-operation",
        "Failed to refresh current user",
        error,
      );
  }, [isError, error]);

  return query;
};
